import { motion } from "framer-motion";
import { ArrowRight, Phone, Check, Star, Quotes } from "@phosphor-icons/react";
import { PHONE, PHONE_TEL, LOCATIONS, IMAGES, REVIEWS, FEATURED_REVIEW } from "@/lib/siteData";

const LINES = ["Building It Right.", "From the Ground Up."];
const TRUST = ["Quality Workmanship", "Reliable Service", "Local Service"];

const REVIEW_COUNT = Array.isArray(REVIEWS) ? REVIEWS.length : 0;
const AVG_RATING = REVIEW_COUNT
  ? (REVIEWS.reduce((sum, r) => sum + (r.rating || 5), 0) / REVIEW_COUNT).toFixed(1)
  : "5.0";

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.35 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  return (
    <section id="top" data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
      <motion.div
        initial={{ scale: 1.12 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
        className="absolute inset-0"
      >
        <img src={IMAGES.hero} alt="Contractor framing a new home build" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/40" />
        <div className="absolute inset-0 bg-ink/30" />
      </motion.div>

      <div className="relative mx-auto max-w-7xl w-full px-5 sm:px-8 pb-16 pt-32">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          data-testid="hero-eyebrow"
          className="text-xs sm:text-sm uppercase tracking-[0.35em] text-gold mb-6"
        >
          {LOCATIONS}
        </motion.p>

        {REVIEW_COUNT > 0 && (
          <motion.a
            href="#reviews"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            data-testid="hero-rating-badge"
            className="group mb-7 inline-flex items-center gap-2.5 border border-white/20 bg-ink/40 backdrop-blur-md px-4 py-2 hover:border-gold transition-colors duration-200"
          >
            <span className="flex gap-0.5" aria-hidden="true">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} size={15} weight="fill" className="text-gold" />
              ))}
            </span>
            <span className="text-sm text-white/90">
              <span className="font-semibold text-white">{AVG_RATING}</span>
              <span className="text-white/50"> · </span>
              Loved by your friends and neighbors
            </span>
          </motion.a>
        )}

        <h1 data-testid="hero-headline" className="font-heading font-bold tracking-tight text-white text-5xl sm:text-7xl lg:text-8xl leading-[0.95] max-w-5xl">
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span custom={i} variants={lineVariant} initial="hidden" animate="show" className="block">
                {i === LINES.length - 1 ? (
                  <>
                    From the Ground <span className="text-gold">Up.</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="mt-8 max-w-xl text-base sm:text-lg text-white/75 leading-relaxed"
        >
          Reliable contracting, remodeling, HVAC, painting and fencing services for homeowners and
          businesses across the greater San Antonio area.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.05 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <a
            href="#contact"
            data-testid="hero-estimate-button"
            className="group inline-flex items-center justify-center gap-2 bg-gold text-ink font-semibold px-8 py-4 hover:bg-gold-hover transition-colors duration-200"
          >
            Get a Free Estimate
            <ArrowRight size={18} weight="bold" className="transition-transform duration-200 group-hover:translate-x-1" />
          </a>
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="hero-call-button"
            className="inline-flex items-center justify-center gap-2 border border-white/25 text-white font-semibold px-8 py-4 hover:border-gold hover:text-gold transition-colors duration-200"
          >
            <Phone weight="fill" size={18} />
            Call B3 Contracting
          </a>
        </motion.div>

        <motion.ul
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.2 }}
          className="mt-12 flex flex-wrap gap-x-8 gap-y-3"
        >
          {TRUST.map((t) => (
            <li key={t} className="flex items-center gap-2 text-sm text-white/80">
              <Check size={16} weight="bold" className="text-gold" />
              {t}
            </li>
          ))}
        </motion.ul>

        {FEATURED_REVIEW?.text && (
          <motion.figure
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.35 }}
            data-testid="hero-featured-review"
            className="mt-10 max-w-md border-l-2 border-gold pl-5"
          >
            <Quotes size={22} weight="fill" className="text-gold/50" />
            <blockquote className="mt-2 text-white/85 text-base sm:text-lg leading-relaxed italic">
              “{FEATURED_REVIEW.text}”
            </blockquote>
            <figcaption className="mt-3 flex items-center gap-2 text-sm text-white/55">
              <span className="flex gap-0.5" aria-hidden="true">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} size={13} weight="fill" className="text-gold" />
                ))}
              </span>
              — {FEATURED_REVIEW.name}
            </figcaption>
          </motion.figure>
        )}
      </div>
    </section>
  );
}
